/**
 * ReviewDetail - a PLAYBOOK as a canvas with panes floating over its edges.
 *
 * (The playbook, `/playbooks/:id` - not a review SESSION, which is
 * `ReviewSessionDetail` and binds its own surface.)
 *
 * WHAT CHANGED, AND WHY.
 *
 * The page was a hand-rolled tab strip over a `calc(100vh - 100px)` scroll
 * box. Two things lived in the wrong place:
 *
 *   • THE AUDIT LOG was a `RecentActivityCard` buried in the Details tab's
 *     right column, plus a hand-rolled `fixed end-0 w-96` drawer with its
 *     own scrim behind the ⋯ menu's "Activity". It is one `activity` pane.
 *   • THE CATEGORIES AND SOURCE DOCUMENT only existed on the Details tab, so
 *     they left the screen the moment someone opened Clause Rules. They are
 *     the `context` pane, readable from every tab.
 *
 * THE LAYERING, outside in:
 *
 *   DetailFrame        breadcrumbs, save, the overflow menu, collaborators
 *     DetailTabBar     the canvas's tabs + the pane rail, full width
 *     DetailPageSurface  the canvas, with the panes over its edges
 *
 * The tab bar is a SIBLING of the surface, not its first child: navigation is
 * the frame around the work, so a docked pane must not sit over it.
 *
 * NO VIEWPORT-RELATIVE HEIGHTS. The `maxHeight: calc(100vh - 100px)` that
 * used to bound the tab content is gone - the surface owns the height and a
 * tab claims what is left with `flex-1 min-h-0`.
 *
 * EDITING A RULE IS NOT A PANE. A clause rule and a global rule are both
 * edited in the FULL global side panel - `showSidePanel({ type:
 * 'editClauseRule' | 'editGlobalRule' })` from `ReviewClauseRules` /
 * `ReviewGlobalRules`, rendering `ClauseRuleFormPanel` / `GlobalRuleFormPanel`
 * through `GlobalSidePanel`. A docked detail container was tried and gave
 * those forms too little room, so this surface declares no detail pane and
 * binds the selection-free hook.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import DetailFrame from '../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../shared/surface/DetailTabBar';
import DetailPageSurface from '../shared/surface/DetailPageSurface';
import PaneRail from '../shared/surface/PaneRail';
import PaneBody from '../shared/surface/PaneBody';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import AccessPane from '../shared/access/AccessPane';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import { normalise } from '../shared/access/accessModel';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import TagInput from '../shared/TailwindComponents/TagInput';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import GlassCard from '../shared/brand/GlassCard';
import { Field, KpiTile } from '../shared/brand/Field';
import {
  PiFloppyDiskLight,
  PiTrashLight,
  PiListBulletsLight,
  PiGlobeLight,
  PiClockCounterClockwiseLight,
  PiInfoLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiQuestionLight,
  PiArrowsClockwiseLight,
  PiUsersThreeLight,
  PiArrowLeftLight,
  PiScalesLight,
  PiCopyLight,
  PiFilePlusLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import {
  fetchReviewDetail,
  updateReview,
  deleteReview,
  extractPlaybook,
  streamPlaybookSourceDocument,
  streamPlaybookSourceDocumentById,
  replacePlaybookSourceDocument,
  removePlaybookSourceDocument,
  PlaybookExtractionMode,
  REVIEW_EXTRACTING_PLAYBOOK,
  addPlaybookCollaborator,
  updatePlaybookCollaborator,
  removePlaybookCollaborator,
  createReview,
} from '../../store/actions/Review/ReviewActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';

import ReviewClauseRules from './ReviewClauseRules';
import ReviewGlobalRules from './ReviewGlobalRules';
import ReviewSessions from './ReviewSessions';
import CreateReviewForm from './CreateReviewForm';
import DeleteTemplateConfirmation from '../Templates/DeleteTemplateConfirmation';
import { usePlaybookExtractionPubSub } from './usePlaybookExtractionPubSub';
import PlaybookSourceUpload from './PlaybookSourceUpload';
import PlaybookExtractionProgress, { PlaybookFailedDocuments } from './PlaybookExtractionProgress';
import { PLAYBOOK_SOURCE_EXTENSIONS, PLAYBOOK_MAX_FILE_BYTES, StagedPlaybookFile } from './playbookExtraction';
import { extractHtmlFromDocx } from '../../utils/docxHandler';
import { PlaybookSharedUser, PlaybookSourceDocument } from '../../types/playbook';
import {
  playbookSourceDocuments,
  playbookSourceDocumentCount,
  toSourceAttachments,
  sourceDocumentOf,
  isLegacySourceDocument,
} from './playbookSourceDocuments';
import { AttachmentList } from '../shared/attachments';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, playbookDetailTourSteps } from '../shared/FeatureTour';
import { PLAYBOOK_SURFACE, PLAYBOOK_TABS, PlaybookTab, usePlaybookWorkspace } from './playbookSurface';
import PlaybookContextPane from './playbookSurface/PlaybookContextPane';

// ─── Component ───────────────────────────────────────────────────────────────

const ReviewDetail: React.FC = () => {
  const { playbookId } = useParams<{ playbookId: string }>();
  const isCreateMode = playbookId === 'new';

  // Render create form for /playbooks/new
  if (isCreateMode) {
    return <CreateReviewForm />;
  }

  return <ReviewDetailInner reviewId={playbookId} />;
};

const ReviewDetailInner: React.FC<{ reviewId: string }> = ({ reviewId }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  // Redux state
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const currentReview = useSelector((s: RootState) => s.review.currentReview);
  const isLoadingDetail = useSelector((s: RootState) => s.review.isLoadingDetail);
  const isExtractingPlaybook = useSelector((s: RootState) => s.review.isExtractingPlaybook);
  const extractionProgress = useSelector((s: RootState) => s.review.extractionProgress);

  /**
   * The workspace: pane layout and the active tab, from the ONE shared hook.
   * Keyed by playbook id AND by user - see `playbookSurface/layout`.
   */
  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
  } = usePlaybookWorkspace(reviewId, {
    // The raw query param: the hook checks it against the surface's declared
    // tabs and falls back to the first, so a stale `?tab=` cannot blank the
    // canvas.
    initialTab: new URLSearchParams(location.search).get('tab') || undefined,
    onTabChange: (tab) =>
      history.replace(`/playbooks/${reviewId}?tab=${encodeURIComponent(tab)}`),
  });

  // Bidirectional sync: keep isExtractingPlaybook in line with the backend extraction_status.
  // ▸ If server says 'processing' but flag is off → activate it (reconnects PubSub)
  // ▸ If server says done/completed/failed but flag is still on → clear it (fixes stale state after navigation)
  useEffect(() => {
    if (!currentReview) return;
    const status = currentReview.extraction_status;

    if (status === 'processing' && !isExtractingPlaybook && !extractionProgress.complete) {
      dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: true });
    } else if (status && status !== 'processing' && isExtractingPlaybook) {
      dispatch({ type: REVIEW_EXTRACTING_PLAYBOOK, payload: false });
    }
  }, [currentReview, currentReview?.extraction_status, isExtractingPlaybook, extractionProgress.complete, dispatch]);

  // PubSub: live extraction progress on group `playbook_{uuid}`
  const extraction = usePlaybookExtractionPubSub(reviewId, isExtractingPlaybook);
  const { beginRun: beginExtraction, reset: resetExtraction } = extraction;

  // Derived: is the UI locked because extraction is running?
  const isProcessing = isExtractingPlaybook;

  // Is this a PONS-owned global playbook? → fully read-only
  const isGlobal = !!currentReview?.is_global;

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'playbook-detail',
    steps: playbookDetailTourSteps,
    readyToShow: !!currentReview && !isLoadingDetail,
    autoStartDelay: 600,
  });

  // Force details tab while extracting so user sees progress.
  useEffect(() => {
    if (isProcessing && activeTab !== 'Details') {
      goToTab('Details');
    }
  }, [isProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * …and take the user to what the run produced when it finishes.
   *
   * The rules are the point of generating a playbook, and the Details tab the
   * run was pinned to is the one place they are NOT. Only for a run watched
   * from start to finish on this page (`beganRunRef`) - opening a playbook
   * that happens to finish elsewhere should not move the tab under anyone.
   */
  const beganRunRef = useRef(false);
  useEffect(() => {
    if (isProcessing) beganRunRef.current = true;
  }, [isProcessing]);

  useEffect(() => {
    if (isProcessing || !beganRunRef.current) return;
    if (extraction.status !== 'completed') return;
    beganRunRef.current = false;
    goToTab('Clause Rules');
  }, [isProcessing, extraction.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agreementTypes, setAgreementTypes] = useState<string[]>([]);
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Playbook state - staged source documents, sent only on "Generate".
  const [playbookFiles, setPlaybookFiles] = useState<StagedPlaybookFile[]>([]);
  const [isStreamingSource, setIsStreamingSource] = useState(false);
  /** A per-row replace or remove is in flight - freezes the whole list's controls. */
  const [isMutatingSource, setIsMutatingSource] = useState(false);

  /**
   * Adding documents to a playbook that already has rules.
   *
   * `isAddingDocuments` reveals the SAME staging UI as first generation —
   * a playbook grows the way it was born, from a set of documents - and
   * `runMode` remembers what the run in flight is doing so the live panel
   * says "Adding to playbook…" rather than "Generating playbook…".
   */
  const [isAddingDocuments, setIsAddingDocuments] = useState(false);
  const [runMode, setRunMode] = useState<PlaybookExtractionMode>('replace');

  // ─── Data loading ──────────────────────────────────────────────────────

  useEffect(() => {
    if (reviewId) {
      dispatch(fetchReviewDetail(reviewId));
    }
  }, [dispatch, reviewId]);

  useEffect(() => {
    if (currentReview) {
      setName(currentReview.name || '');
      setDescription(currentReview.description || '');
      setAgreementTypes(currentReview.agreement_types || []);
      setJurisdictions(currentReview.jurisdictions || []);
    }
  }, [currentReview]);

  // ─── Save ──────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!currentReview || !name.trim()) return;
    setIsSaving(true);
    try {
      await dispatch(
        updateReview(currentReview.uuid, {
          name: name.trim(),
          description,
          agreement_types: agreementTypes,
          jurisdictions,
        }) as any,
      );
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, currentReview, name, description, agreementTypes, jurisdictions]);

  // ─── Delete ────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    if (!currentReview) return;
    dispatch(
      showModal({
        type: 'custom' as any,
        message: t('Are you sure you want to delete this playbook?'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        width: '500px',
        showModal: true,
        content: (
          <DeleteTemplateConfirmation
            count={1}
            entityLabel={t('playbook')}
            description={t('This will permanently delete this playbook, including all clause rules and global rules. This action cannot be undone.')}
            showDeleteSessions={true}
            onConfirm={async (deleteSessions?: boolean) => {
              const success = await dispatch(deleteReview(currentReview.uuid, deleteSessions || false) as any);
              if (success) {
                dispatch(hideModal());
                history.push('/templates?type=playbooks');
              }
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
      }),
    );
  }, [dispatch, currentReview, history, t]);

  // ─── Playbook extraction ───────────────────────────────────────────────


  /**
   * One submit, all staged documents. The run is seeded with the file names
   * first so the per-file progress list is on screen before the socket is up
   * - otherwise the panel sits empty for the length of the upload.
   *
   * `mode` is the only difference between generating and adding: the same
   * upload, the same group, the same progress panel. In `augment` the engine
   * merges into the existing rules instead of rewriting them.
   */
  const handleExtractPlaybook = useCallback(
    async (files: File[], mode: PlaybookExtractionMode = 'replace') => {
      if (!currentReview || files.length === 0) return;
      setRunMode(mode);
      beginExtraction(files.map((f) => f.name));
      const result = await dispatch(extractPlaybook(currentReview.uuid, files, mode) as any);
      if (result) {
        setPlaybookFiles([]);
        setIsAddingDocuments(false);
        // The PubSub hook refetches the detail on completion, which is what
        // repopulates the clause and global rule lists - merged, in augment
        // mode, since the backend returns the whole set.
      } else {
        resetExtraction();
      }
    },
    [dispatch, currentReview, beginExtraction, resetExtraction],
  );

  const handleAddDocuments = useCallback(
    (files: File[]) => handleExtractPlaybook(files, 'augment'),
    [handleExtractPlaybook],
  );

  // ─── View / Download source documents ───────────────────────────────────

  /**
   * Every document this playbook was extracted from - the full provenance
   * list, or the single legacy row synthesized from `source_document_name`
   * when the payload predates it.
   */
  const sourceDocuments = useMemo(
    () => playbookSourceDocuments(currentReview),
    [currentReview],
  );
  const sourceDocumentCount = useMemo(
    () => playbookSourceDocumentCount(currentReview),
    [currentReview],
  );
  const sourceAttachments = useMemo(
    () => toSourceAttachments(sourceDocuments),
    [sourceDocuments],
  );


  /**
   * Stream one source document - by its own uuid when the backend recorded
   * provenance for it, through the playbook-wide endpoint when the row is a
   * legacy stand-in with no uuid. Defaults to the first document, which is
   * what the context pane's single View button asks for.
   */
  const streamSource = useCallback(
    (sourceDoc: PlaybookSourceDocument | null, disposition: 'inline' | 'attachment') => {
      if (!currentReview) return Promise.resolve(null);
      if (sourceDoc && !isLegacySourceDocument(sourceDoc)) {
        return dispatch(
          streamPlaybookSourceDocumentById(currentReview.uuid, sourceDoc.uuid as string, disposition) as any,
        );
      }
      return dispatch(streamPlaybookSourceDocument(currentReview.uuid, disposition) as any);
    },
    [dispatch, currentReview],
  );

  const handleViewSourceDocument = useCallback(async (sourceDoc?: PlaybookSourceDocument) => {
    if (!currentReview) return;
    const target = sourceDoc || sourceDocuments[0] || null;
    setIsStreamingSource(true);
    try {
      const result = await streamSource(target, 'inline');
      if (result?.success && result.blob) {
        const ct = (result.contentType || '').toLowerCase();
        const docName = target?.file_name || currentReview.source_document_name || t('Playbook Document');
        const ext = docName.split('.').pop()?.toLowerCase() || '';
        const isPdf = ct.includes('pdf') || ext === 'pdf';
        const isDocx = ext === 'docx' || ct.includes('officedocument') || ct.includes('msword');

        if (isDocx) {
          // Convert DOCX to HTML before opening side panel
          try {
            const arrayBuffer = await (result.blob as Blob).arrayBuffer();
            const html = await extractHtmlFromDocx(arrayBuffer);
            if (html) {
              const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; line-height: 1.6; color: #1f2937; background: #fff; }
  table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  td, th { border: 1px solid #d1d5db; padding: 8px; }
  img { max-width: 100%; height: auto; }
</style></head><body>${html}</body></html>`;
              dispatch(showSidePanel({
                type: 'fileReaderTst',
                showSidePanel: true,
                size: 'lg',
                content: fullHtml,
                filename: docName,
                loading: false,
                message: docName,
                subMsg: t('Source Document Preview'),
              }));
            }
          } catch (convErr) {
            console.error('DOCX conversion failed:', convErr);
            // Fallback: pass blob URL, CustomFileReader will handle it
            const blobUrl = URL.createObjectURL(result.blob);
            dispatch(showSidePanel({
              type: 'fileReaderTst',
              showSidePanel: true,
              size: 'lg',
              fileUrl: blobUrl,
              filename: docName,
              loading: false,
              message: docName,
              subMsg: t('Source Document Preview'),
            }));
          }
        } else {
          // PDF and other formats: pass blob URL directly
          const blobUrl = URL.createObjectURL(result.blob);
          dispatch(showSidePanel({
            type: 'fileReaderTst',
            showSidePanel: true,
            size: 'lg',
            fileUrl: blobUrl,
            isPdf,
            filename: docName,
            loading: false,
            message: docName,
            subMsg: t('Source Document Preview'),
          }));
        }
      }
    } finally {
      setIsStreamingSource(false);
    }
  }, [currentReview, sourceDocuments, streamSource, dispatch, t]);

  const handleDownloadSourceDocument = useCallback(async (sourceDoc?: PlaybookSourceDocument) => {
    if (!currentReview) return;
    const target = sourceDoc || sourceDocuments[0] || null;
    const result = await streamSource(target, 'attachment');
    if (result?.success && result.blob) {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = target?.file_name || currentReview.source_document_name || 'playbook_document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [currentReview, sourceDocuments, streamSource]);

  // ─── Replace the whole playbook (legacy single-document fallback) ───────

  /**
   * The ORIGINAL replace: a new document, and the rules regenerated from it.
   *
   * Only reachable now for a playbook whose provenance is a single LEGACY row
   * - one with no uuid, which neither the per-document replace endpoint nor
   * the delete endpoint can address. Everything else replaces and removes
   * per row, below, without touching the extracted rules.
   */
  const handleUploadNewPlaybook = useCallback(() => {
    if (!currentReview) return;
    dispatch(showModal({
      type: 'replacePlaybookDocument' as any,
      message: t('Replace Source Document'),
      subMsg: t('Upload a new document to replace the current one. AI extraction will re-run automatically to update all rules.'),
      icon: <PiArrowsClockwiseLight className="w-5 h-5 text-info-600" />,
      showModal: true,
      maxFiles: 1,
      allowedExtensions: PLAYBOOK_SOURCE_EXTENSIONS,
      // Same 50MB ceiling as staged generation - without it the modal's
      // uploader falls back to its 10MB default and refuses ordinary
      // playbook PDFs on the replace path alone.
      maxSizeBytes: PLAYBOOK_MAX_FILE_BYTES,
      onFileSelected: (file: File) => {
        setRunMode('replace');
        beginExtraction([file.name]);
        dispatch(extractPlaybook(currentReview.uuid, [file], 'replace') as any);
      },
    }));
  }, [dispatch, currentReview, beginExtraction, t]);

  // ─── Replace / Remove ONE source document ───────────────────────────────

  /**
   * Is this playbook's provenance nothing but ONE legacy row?
   *
   * That row has no uuid, so it can only be handled the old way - replace the
   * document and re-extract. In that one case the list carries no per-row
   * Replace/Remove and the card keeps its single Replace button, so there is
   * exactly one affordance rather than two that behave differently.
   */
  const isLegacySingleSource =
    sourceDocuments.length === 1 && isLegacySourceDocument(sourceDocuments[0]);

  /**
   * Swap one document for a newer file. The rules are NOT regenerated - this
   * corrects provenance (wrong revision, unreadable scan), it does not rebuild
   * the playbook. A legacy row falls back to the whole-playbook path above.
   */
  const handleReplaceSourceDocument = useCallback(
    (sourceDoc?: PlaybookSourceDocument | null) => {
      if (!currentReview) return;

      if (!sourceDoc || isLegacySourceDocument(sourceDoc)) {
        handleUploadNewPlaybook();
        return;
      }

      const documentUuid = sourceDoc.uuid as string;
      const documentName = sourceDoc.file_name || t('this document');

      dispatch(showModal({
        type: 'replacePlaybookDocument' as any,
        message: t('Replace Document'),
        subMsg: t('Upload a new file to take the place of "{{name}}". The playbook\'s rules are left exactly as they are.', { name: documentName }),
        icon: <PiArrowsClockwiseLight className="w-5 h-5 text-info-600" />,
        showModal: true,
        maxFiles: 1,
        allowedExtensions: PLAYBOOK_SOURCE_EXTENSIONS,
        // The same 50MB ceiling as staged generation - the modal's uploader
        // otherwise falls back to 10MB and refuses ordinary playbook PDFs.
        maxSizeBytes: PLAYBOOK_MAX_FILE_BYTES,
        confirmLabel: t('Replace document'),
        onFileSelected: async (file: File) => {
          setIsMutatingSource(true);
          try {
            await dispatch(
              replacePlaybookSourceDocument(currentReview.uuid, documentUuid, file) as any,
            );
          } finally {
            setIsMutatingSource(false);
          }
        },
      }));
    },
    [dispatch, currentReview, handleUploadNewPlaybook, t],
  );

  /**
   * Drop one document from the playbook's provenance.
   *
   * Allowed even for the last one - the rules outlive the documents they came
   * from, and documents can be added back - but the confirmation says plainly
   * that the playbook will be left with no source.
   */
  const handleRemoveSourceDocument = useCallback(
    (sourceDoc?: PlaybookSourceDocument | null) => {
      if (!currentReview || !sourceDoc || isLegacySourceDocument(sourceDoc)) return;

      const documentUuid = sourceDoc.uuid as string;
      const documentName = sourceDoc.file_name || '';
      const isLast = sourceDocuments.length <= 1;

      dispatch(showModal({
        type: 'confirmation',
        showModal: true,
        title: t('Remove this document from the playbook?'),
        message: documentName
          ? t('"{{name}}" will no longer be listed as a source for this playbook.', { name: documentName })
          : t('This document will no longer be listed as a source for this playbook.'),
        subText: isLast
          ? t('This is the last source document — the playbook will have none. The rules already extracted are kept, and you can add documents again at any time.')
          : t('The rules already extracted from it are kept.'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        variant: 'danger',
        confirmLabel: t('Remove'),
        onConfirm: async () => {
          setIsMutatingSource(true);
          try {
            await dispatch(
              removePlaybookSourceDocument(currentReview.uuid, documentUuid) as any,
            );
          } finally {
            setIsMutatingSource(false);
          }
        },
      }));
    },
    [dispatch, currentReview, sourceDocuments.length, t],
  );

  // ─── Help guide modal ──────────────────────────────────────────────────

  const handleOpenHelp = useCallback(() => {
    dispatch(showModal({
      showModal: true,
      type: 'playbookHelp' as any,
      message: t('Understanding Playbooks'),
      icon: <PiQuestionLight className="w-5 h-5 text-gray-500" />,
    }));
  }, [dispatch, t]);

  // ─── Clone global playbook ─────────────────────────────────────────────

  const [isCloning, setIsCloning] = useState(false);

  const handleClonePlaybook = useCallback(async () => {
    if (!currentReview) return;
    setIsCloning(true);
    try {
      const result = await dispatch(
        createReview({
          name: `${currentReview.name} (${t('Copy')})`,
          description: currentReview.description || '',
          agreement_types: currentReview.agreement_types || [],
          jurisdictions: currentReview.jurisdictions || [],
          clause_rules: currentReview.clause_rules || [],
          global_rules: currentReview.global_rules || [],
        }) as any,
      );
      if (result?.uuid) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Playbook cloned successfully'),
            modalType: 'success',
            duration: 3000,
          }),
        );
        history.push(`/playbooks/${result.uuid}`);
      }
    } catch {
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to clone playbook'),
          modalType: 'error',
          duration: 3000,
        }),
      );
    } finally {
      setIsCloning(false);
    }
  }, [dispatch, currentReview, history, t]);

  // ─── Manage Collaborators ──────────────────────────────────────────────

  const handleManageCollaborators = useCallback(() => {
    if (!currentReview) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage Access'),
      resourceUuid: currentReview.uuid,
      resourceType: 'review',
      collaborators: currentReview.has_access || [],
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addPlaybookCollaborator(uuid, { collaborator_id: userUuid, access: access as any }) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removePlaybookCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updatePlaybookCollaborator(uuid, collabUuid, { access: access as any }) as any);
      },
      onUpdate: () => {
        dispatch(fetchReviewDetail(currentReview.uuid));
      },
    }));
  }, [dispatch, currentReview, t, userInfo?.UserUID]);

  // ─── Breadcrumbs ───────────────────────────────────────────────────────

  const breadcrumbs = useMemo(() => [
    { name: t('Templates'), href: '/templates?type=playbooks', current: false },
    { name: currentReview?.name || t('Loading...'), href: `/playbooks/${reviewId}`, current: true },
  ], [currentReview, reviewId, t]);

  // ─── Tabs ──────────────────────────────────────────────────────────────

  /* The VALUES come from the surface config - one vocabulary for the bar, the
     URL and `goToTab`. The labels, icons and counts are the page's. */
  const tabs: TabItem[] = useMemo(() => {
    const meta: Record<PlaybookTab, { label: string; icon: React.ReactNode; count?: number }> = {
      Details: { label: t('Details'), icon: <PiInfoLight className="w-4 h-4" /> },
      'Clause Rules': {
        label: t('Clause Rules'),
        icon: <PiListBulletsLight className="w-4 h-4" />,
        count: currentReview?.clause_rules?.length,
      },
      'Global Rules': {
        label: t('Global Rules'),
        icon: <PiGlobeLight className="w-4 h-4" />,
        count: currentReview?.global_rules?.length,
      },
      Reviews: {
        label: t('Reviews'),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
        count: currentReview?.recent_reviews?.length,
      },
    };

    return PLAYBOOK_TABS.map((value) => {
      const { label, icon, count } = meta[value];
      return {
        value,
        label,
        icon,
        // Everything but Details is locked while extraction rewrites the rules.
        disabled: isProcessing && value !== 'Details',
        renderedLabel: count ? (
          <span className="flex items-center gap-1.5">
            {label}
            <Badge color="gray">{count}</Badge>
          </span>
        ) : undefined,
      };
    });
  }, [currentReview, isProcessing, t]);

  // ─── Header actions ────────────────────────────────────────────────────

  const headerActions = useMemo(() => {
    if (activeTab !== 'Details' || isGlobal) return [];
    return [
      {
        label: t('Save Changes'),
        icon: <PiFloppyDiskLight className="w-4 h-4" />,
        onClick: handleSave,
        disabled: !name.trim() || isSaving || isProcessing,
      },
    ];
  }, [activeTab, handleSave, name, isSaving, isGlobal, isProcessing, t]);

  const dropdownActions = useMemo(() => {
    if (isGlobal) return [];
    return [
      /* NO "Manage Access" HERE - the rail's Access pill (badged with the
         count) raises the pane that lists who this playbook is shared with,
         and the pane's own button opens the editor. */
      {
        // The activity log is a PANE now, not a drawer: the menu item raises it.
        label: t('Activity'),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
        onClick: () => surfaceApi.openPane('activity'),
        disabled: isProcessing,
      },
      {
        label: t('Delete Playbook'),
        icon: <PiTrashLight className="w-4 h-4" />,
        onClick: handleDelete,
        isDestructive: true,
        disabled: isProcessing,
      },
    ];
  }, [handleDelete, isProcessing, isGlobal, t, surfaceApi]);

  // ─── Loading state ─────────────────────────────────────────────────────

  // Stale-data guard: when navigating between playbooks, currentReview still
  // holds the previous playbook's data until the new fetch resolves. Without
  // this check, the user briefly sees the old playbook before the new one
  // loads. Treat both "still fetching" and "data is for a different playbook"
  // as loading states.
  const isStaleData = !!currentReview && currentReview.uuid !== reviewId;
  if (isLoadingDetail || isStaleData) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Details" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" />
          </div>
        </div>
      </DetailFrame>
    );
  }

  if (!currentReview) {
    return <DetailFrame breadcrumbs={breadcrumbs} actions={[]} notFound><div /></DetailFrame>;
  }

  // ─── Collaborators for header ──────────────────────────────────────────

  /**
   * Is there anything to add TO? A playbook with rules or a source document
   * can be augmented; an empty one is still on its first generation, which
   * the staging area above already offers.
   */
  const hasPlaybookContent =
    sourceDocuments.length > 0 ||
    (currentReview.clause_rules?.length || 0) > 0 ||
    (currentReview.global_rules?.length || 0) > 0;

  // Resolved through `accessModel` rather than key-by-key: `has_access` rows
  // spell the picture `profile_picture` on some endpoints and `avatar_url` on
  // others, and a hand-written mapping only ever caught one of them.
  const collaborators = (currentReview.has_access || []).map((u: PlaybookSharedUser) => {
    const person = normalise(u);
    return {
      user_uuid: String(u.user_id ?? person.id),
      name: person.name,
      initials: person.initials,
      access: u.access || person.access,
      email: person.email || null,
      avatar_url: person.avatar,
    };
  });

  return (
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={headerActions}
      dropdownActions={dropdownActions}
      /* The header faces RAISE THE ACCESS PANE - see `additionalHeaderElements`.
         `DetailFrame`'s own stack is decorative (not pressable), so it is not
         used here. */
      collaborators={[]}
      additionalHeaderElements={
        <>
          <ProjectBadges resourceType="review" resourceUuid={reviewId} />
          <PendingAccessRequests resourceType="review" resourceUuid={reviewId} />
          {!isGlobal && (
            <CollaboratorStack
              collaborators={collaborators}
              onClick={() => surfaceApi.openPane('access')}
              testId="playbook-collaborator-stack"
            />
          )}
          <Button
            onClick={handleOpenHelp}
            title={t('What is a Playbook?')}
            size='small'
            variant='outline'
            className='py-1.5'
          >
            <PiQuestionLight className="w-3.5 h-3.5" />
          </Button>
        </>
      }
    >
      <div className="flex-1 flex flex-col min-h-0">
      {/* Back button - only for PONS global playbooks */}
      {isGlobal && (
        <div className="px-4 pt-3 pb-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => history.push('/templates?type=playbooks')}
            className="flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg-body transition-colors"
          >
            <PiArrowLeftLight className="w-4 h-4 rtl:-scale-x-100" />
            {t('Back to Playbooks')}
          </button>
        </div>
      )}
      {/* Global playbook banner */}
      {isGlobal && (
        <div className="mx-4 mt-2 flex items-center justify-between gap-3 rounded-lg bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 px-4 py-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <PiInfoLight className="w-5 h-5 text-info-600 dark:text-info-400 shrink-0" />
            <span className="text-sm text-info-800 dark:text-info-200">
              {t('This is a PONS-managed playbook. Rules are read-only.')}
            </span>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={handleClonePlaybook}
            icon={<PiCopyLight className="w-4 h-4" />}
            disabled={isCloning}
            isLoading={isCloning}
          >
            {t('Clone to My Playbooks')}
          </Button>
        </div>
      )}

      {/*
        THE TABS ARE THEIR OWN ROW, FULL WIDTH, ABOVE THE SURFACE - so a
        docked pane floats below them rather than over them.
      */}
      <div data-tour-id="playbook-tab-bar" className="flex-shrink-0 w-full">
        <DetailTabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabClick={goToTab}
          tourId="playbook-tabs-left"
          actions={(
            <PaneRail
              panels={panels}
              ariaLabel={t('Panels')}
              /* Icons drop to labels-off below xl so a laptop shares the row. */
              labels="auto"
              /* Straight from the surface config: every pane that asked for a
                 rail toggle gets one, so a new pane cannot be forgotten here. */
              items={PLAYBOOK_SURFACE.railItems({
                t,
                icons: {
                  context: <PiInfoLight />,
                  access: <PiUsersThreeLight />,
                  activity: <PiClockCounterClockwiseLight />,
                },
                badges: { access: collaborators.length },
                /* A PONS-managed playbook has no sharing of its own - it is
                   everyone's, read-only - so the pill would open onto an
                   empty list. */
                omit: isGlobal ? ['access' as const] : [],
              })}
            />
          )}
        />
      </div>

      <DetailPageSurface
        surface={PLAYBOOK_SURFACE}
        layoutApi={panels}
        panes={[
          {
            id: 'context',
            title: t('Context'),
            icon: <PiInfoLight />,
            body: (
              <PlaybookContextPane
                playbook={currentReview}
                onViewSource={handleViewSourceDocument}
                onDownloadSource={handleDownloadSourceDocument}
                isStreamingSource={isStreamingSource}
                onOpenTab={goToTab}
              />
            ),
          },
          {
            id: 'access',
            title: t('Access'),
            icon: <PiUsersThreeLight />,
            body: (
              <AccessPane
                collaborators={currentReview.has_access || []}
                onManageAccess={handleManageCollaborators}
                canManage={!isGlobal}
              />
            ),
          },
          {
            id: 'activity',
            title: t('Activity'),
            icon: <PiClockCounterClockwiseLight />,
            /* A thunk: the log fetches on mount, so it is built only while
               the pane is on screen. No `hideHeader` - it reads `useInPane()`
               and renders its body alone. */
            body: () => (
              <PaneBody>
                <AuditLogPanel
                  entityType="review"
                  entityId={reviewId}
                  entityName={currentReview.name || ''}
                  onClose={() => panels.setVisible('activity', false)}
                />
              </PaneBody>
            ),
          },
        ]}
      >
        {/* Details tab */}
        {activeTab === 'Details' && (
          <div className="details-canvas flex-1 min-h-0 overflow-y-auto custom-scrollbar-main animate-fade-in">
            <div className="details-pad mx-auto w-full max-w-6xl px-6 py-8 space-y-8">
            {/* Stats Bar */}
            {(() => {
              const clauseRules = currentReview.clause_rules || [];
              const globalRules = currentReview.global_rules || [];
              const sessions = currentReview.recent_reviews || [];
              const avgRisk = clauseRules.length > 0
                ? (clauseRules.reduce((sum, r) => sum + (r.risk_weight || 0), 0) / clauseRules.length).toFixed(1)
                : '—';
              const lowCount = clauseRules.filter((r) => r.risk_weight >= 1 && r.risk_weight <= 3).length;
              const medCount = clauseRules.filter((r) => r.risk_weight >= 4 && r.risk_weight <= 7).length;
              const highCount = clauseRules.filter((r) => r.risk_weight >= 8 && r.risk_weight <= 10).length;
              const totalRisk = lowCount + medCount + highCount;

              return (
                <GlassCard padding="none" className="overflow-hidden">
                  <div className="details-kpis divide-hairline-soft">
                    <KpiTile icon={<PiListBulletsLight />} tone="info" value={clauseRules.length} label={t('Clause Rules')} />
                    <KpiTile icon={<PiGlobeLight />} value={globalRules.length} label={t('Global Rules')} />
                    <KpiTile icon={<PiClockCounterClockwiseLight />} value={sessions.length} label={t('Review Sessions')} />
                    <KpiTile icon={<PiScalesLight />} tone="warning" value={avgRisk} label={t('Avg Risk Weight')} />
                  </div>
                  {totalRisk > 0 && (
                    <div className="px-4 pb-3">
                      <div className="flex h-1.5 rounded-full overflow-hidden bg-surface-sunken">
                        {lowCount > 0 && (
                          <div
                            className="bg-success-500 transition-all"
                            style={{ width: `${(lowCount / totalRisk) * 100}%` }}
                            title={`${t('Low')}: ${lowCount}`}
                          />
                        )}
                        {medCount > 0 && (
                          <div
                            className="bg-warning-400 transition-all"
                            style={{ width: `${(medCount / totalRisk) * 100}%` }}
                            title={`${t('Medium')}: ${medCount}`}
                          />
                        )}
                        {highCount > 0 && (
                          <div
                            className="bg-danger-500 transition-all"
                            style={{ width: `${(highCount / totalRisk) * 100}%` }}
                            title={`${t('High')}: ${highCount}`}
                          />
                        )}
                      </div>
                      <div className="flex gap-3 mt-1.5">
                        <span className="flex items-center gap-1 text-[10px] text-fg-muted"><span className="w-2 h-2 rounded-full bg-success-500 inline-block" />{t('Low')} ({lowCount})</span>
                        <span className="flex items-center gap-1 text-[10px] text-fg-muted"><span className="w-2 h-2 rounded-full bg-warning-400 inline-block" />{t('Medium')} ({medCount})</span>
                        <span className="flex items-center gap-1 text-[10px] text-fg-muted"><span className="w-2 h-2 rounded-full bg-danger-500 inline-block" />{t('High')} ({highCount})</span>
                      </div>
                    </div>
                  )}
                </GlassCard>
              );
            })()}

            {/* Two-column grid: Metadata (left) + Source Document (right) */}
            <div className="details-body gap-6 items-start">

            {/* Left column: Playbook info + Categories */}
            <div className="min-w-0 space-y-8">

            {/* Card 1: Playbook Information */}
            <GlassCard title={t('Playbook Information')} padding="lg" data-tour-id="playbook-details-section">
              <div className="space-y-4">
              {isGlobal ? (
                <>
                  <Field label={t('Playbook Name')} value={name} emptyText="—" />
                  {description && (
                    <Field label={t('Description')}>
                      <p className="text-sm text-fg-body whitespace-pre-wrap">{description}</p>
                    </Field>
                  )}
                </>
              ) : (
                <>
                  <Input
                    type="text"
                    id="review-name"
                    label={t('Playbook Name')}
                    placeholder={t('e.g. NDA Review Standard')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                  <Textarea
                    id="review-description"
                    label={t('Description')}
                    placeholder={t('Describe what this review checks for...')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    disabled={isProcessing}
                  />
                </>
              )}
              </div>
            </GlassCard>

            {/* Card 2: Categories */}
            <GlassCard title={t('Categories')} padding="lg">
              {isGlobal ? (
                <div className="details-grid-2 gap-4">
                  {agreementTypes.length > 0 && (
                    <Field label={t('Agreement Types')}>
                      <div className="flex flex-wrap gap-1">
                        {agreementTypes.map((at, i) => <Badge key={i} color="blue" className="capitalize">{at}</Badge>)}
                      </div>
                    </Field>
                  )}
                  {jurisdictions.length > 0 && (
                    <Field label={t('Jurisdictions')}>
                      <div className="flex flex-wrap gap-1">
                        {jurisdictions.map((j, i) => <Badge key={i} color="purple" className="capitalize">{j}</Badge>)}
                      </div>
                    </Field>
                  )}
                </div>
              ) : (
                <div className="details-grid-2 gap-4">
                  <TagInput
                    id="agreement-types"
                    label={t('Agreement Types')}
                    placeholder={t('Type and press Enter (e.g. NDA, SPA, Employment)')}
                    tags={agreementTypes}
                    onChange={setAgreementTypes}
                    disabled={isProcessing}
                  />
                  <TagInput
                    id="jurisdictions"
                    label={t('Jurisdictions')}
                    placeholder={t('Type and press Enter (e.g. Norway, EU, US-NY)')}
                    tags={jurisdictions}
                    onChange={setJurisdictions}
                    disabled={isProcessing}
                  />
                </div>
              )}
            </GlassCard>

            </div>{/* end left column */}

            {/* Right column: Source Document */}
            <div className="details-side min-w-0 space-y-4">
            {!isGlobal && <div className="space-y-3" data-tour-id="playbook-source-document">
              <h3 className="text-base font-medium text-fg-strong">
                {sourceDocumentCount > 1
                  ? t('Source Documents ({{count}})', { count: sourceDocumentCount })
                  : t('Source Document')}
              </h3>
              <p className="text-sm text-fg-muted">
                {t('Upload your firm\'s playbook or standards document. PONS will automatically extract clause rules and global rules.')}
              </p>

              {/*
                EVERY document the playbook was extracted from, not just the
                first. Rows go through the shared attachment list so a playbook
                source reads like a file anywhere else in the app; the name
                opens it inline and the arrow downloads it. `sourceDocumentOf`
                hands the raw row back, which is what decides whether the
                stream goes to the per-document endpoint or the legacy
                playbook-wide one.
              */}
              {sourceAttachments.length > 0 && !isExtractingPlaybook && (
                <GlassCard padding="md" className="space-y-3">
                  <AttachmentList
                    attachments={sourceAttachments}
                    density="pane"
                    busy={isStreamingSource || isMutatingSource}
                    onOpen={(attachment) => handleViewSourceDocument(sourceDocumentOf(attachment) || undefined)}
                    onDownload={(attachment) => handleDownloadSourceDocument(sourceDocumentOf(attachment) || undefined)}
                    /* Replace sits with the row it replaces - one document at a
                       time, never the whole playbook. Suppressed on the pure
                       legacy row, which the card's own button handles. */
                    rowActions={(attachment) => (
                      isLegacySingleSource ? null : (
                        <button
                          type="button"
                          aria-label={t('Replace')}
                          title={t('Replace')}
                          disabled={isStreamingSource || isMutatingSource}
                          onClick={() => handleReplaceSourceDocument(sourceDocumentOf(attachment))}
                          className="p-1.5 rounded-md text-fg-muted hover:text-champagne transition-colors duration-200 ease-slide disabled:opacity-50 flex-shrink-0"
                        >
                          <PiArrowsClockwiseLight className="h-3.5 w-3.5" />
                        </button>
                      )
                    )}
                    onRemove={(attachment) => handleRemoveSourceDocument(sourceDocumentOf(attachment))}
                    /* A legacy row has no uuid for the delete endpoint to
                       address, so it gets no Remove control. */
                    canRemove={(attachment) => !isLegacySourceDocument(sourceDocumentOf(attachment))}
                  />
                  <div className="flex items-center gap-2 pt-2 border-t border-hairline-soft">
                    <span className="text-xs text-fg-muted">
                      {t('Click a document to preview it.')}
                    </span>
                    <div className="flex-1" />
                    {isLegacySingleSource && (
                      <Button
                        variant="outline"
                        size="small"
                        onClick={handleUploadNewPlaybook}
                        icon={<PiArrowsClockwiseLight className="w-4 h-4" />}
                        disabled={isMutatingSource}
                      >
                        {t('Replace')}
                      </Button>
                    )}
                  </div>
                </GlassCard>
              )}

              {/* Live generation: steps + per-file sub-progress */}
              {isExtractingPlaybook && (
                <PlaybookExtractionProgress
                  run={extraction}
                  /* Centred in the tab: while a run is live everything else in
                     this section is hidden, so the card is the content. */
                  layout="centered"
                  /* Augmenting is not generating: nothing is being rebuilt,
                     rules are being merged into what is already there. */
                  title={runMode === 'augment' ? t('Adding to playbook…') : undefined}
                  completedTitle={runMode === 'augment' ? t('Documents added to playbook') : undefined}
                  failedTitle={runMode === 'augment' ? t('Adding documents failed') : undefined}
                  onDismissFailedDocuments={extraction.dismissFailedDocuments}
                />
              )}

              {/* Finished run: outcome, plus any documents that couldn't be read */}
              {!isExtractingPlaybook && extraction.status !== 'idle' && (
                <>
                  {extractionProgress.complete && (
                    <div className={clsx(
                      'p-3 rounded-lg border flex items-center gap-2',
                      extractionProgress.ok
                        ? 'bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800'
                        : 'bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800'
                    )}>
                      {extractionProgress.ok ? (
                        <PiCheckCircleLight className="w-5 h-5 text-success-600 shrink-0" />
                      ) : (
                        <PiWarningCircleLight className="w-5 h-5 text-danger-600 shrink-0" />
                      )}
                      <span className={clsx(
                        'text-sm',
                        extractionProgress.ok ? 'text-success-800 dark:text-success-200' : 'text-danger-800 dark:text-danger-200'
                      )}>
                        {extractionProgress.summary || (extractionProgress.ok ? t('Extraction complete!') : t('Extraction failed'))}
                      </span>
                    </div>
                  )}
                  <PlaybookFailedDocuments
                    documents={extraction.failedDocuments}
                    onDismiss={extraction.dismissFailedDocuments}
                  />
                </>
              )}

              {/* Staging area - shown only when no source doc exists */}
              {sourceAttachments.length === 0 && !isExtractingPlaybook && (
                <PlaybookSourceUpload
                  value={playbookFiles}
                  onChange={setPlaybookFiles}
                  onSubmit={handleExtractPlaybook}
                  isSubmitting={isExtractingPlaybook}
                  submitLabel={t('Generate playbook')}
                  label={t('Playbook documents')}
                />
              )}

              {/*
                ADDING TO A PLAYBOOK THAT ALREADY HAS RULES.

                Distinct from Replace, which throws the existing rules away:
                this merges. It only appears once there is something to add to
                - a source document or rules - and it opens the SAME staging
                UI, so the second batch behaves exactly like the first.
              */}
              {hasPlaybookContent && !isExtractingPlaybook && (
                <div className="space-y-3">
                  {!isAddingDocuments ? (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => setIsAddingDocuments(true)}
                      icon={<PiFilePlusLight className="w-4 h-4" />}
                      data-tour-id="playbook-add-documents"
                    >
                      {t('Add documents')}
                    </Button>
                  ) : (
                    <GlassCard
                      padding="md"
                      title={t('Add documents')}
                      subtitle={t('New rules are merged into this playbook. Nothing existing is removed.')}
                      actions={(
                        <Button
                          variant="neutral"
                          size="small"
                          onClick={() => {
                            setIsAddingDocuments(false);
                            setPlaybookFiles([]);
                          }}
                        >
                          {t('Cancel')}
                        </Button>
                      )}
                    >
                      <PlaybookSourceUpload
                        value={playbookFiles}
                        onChange={setPlaybookFiles}
                        onSubmit={handleAddDocuments}
                        isSubmitting={isExtractingPlaybook}
                        submitLabel={t('Add to playbook')}
                        label={t('Documents to add')}
                      />
                    </GlassCard>
                  )}
                </div>
              )}
            </div>}
            </div>{/* end right column */}
            </div>{/* end grid */}
            </div>{/* end details-pad */}
          </div>
        )}

        {/* Clause Rules tab */}
        {activeTab === 'Clause Rules' && (
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <ReviewClauseRules
              reviewUuid={currentReview.uuid}
              clauseRules={currentReview.clause_rules || []}
              readOnly={isGlobal}
            />
          </div>
        )}

        {/* Global Rules tab */}
        {activeTab === 'Global Rules' && (
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <ReviewGlobalRules
              reviewUuid={currentReview.uuid}
              globalRules={currentReview.global_rules || []}
              readOnly={isGlobal}
            />
          </div>
        )}

        {/* Sessions tab */}
        {activeTab === 'Reviews' && (
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <ReviewSessions
              reviewUuid={currentReview.uuid}
              sessions={currentReview.recent_reviews || []}
            />
          </div>
        )}
      </DetailPageSurface>
      </div>

      <FeatureTour
        steps={playbookDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
  );
};

export default ReviewDetail;
